import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  TextField,
  Avatar,
  Tabs,
  Tab,
  Pagination,
  InputAdornment,
  Stack,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Collapse,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Skeleton,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Edit,
  Visibility,
  Person,
  AttachMoney,
  Comment,
  DeleteSweep,
  ArrowBack,
  Search,
  QrCode2,
  Download,
  Print,
  Close,
  Image as ImageIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Receipt,
  FileCopy,
  CheckCircle,
  Error as ErrorIcon,
  Cancel,
  AccountBalanceWallet,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useDeleteCommentMutation,
  useGenerateQRMutation,
  useGenerateInvoiceMutation,
  useGenerateCombinedMutation,
  useLazyGetQrImageQuery,
  useCancelOrderMutation,
  ORDER_STATUS_OPTIONS,
  Order,
  OrderComment,
  determineOrderType,
  GenerateCombinedResponse,
} from '../../features/admin/OrderlistApi.ts';

import { useListWarehousesQuery } from '../../features/admin/warehouseApi.ts';
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-details-tabpanel-${index}`}
      aria-labelledby={`order-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash_on_delivery', label: 'Cash on Delivery' },
  { value: 'pay_later', label: 'Pay Later' },
  { value: 'online_payment', label: 'Online Payment' },
];

const CANCEL_REASON_OPTIONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'out_of_stock', label: 'Out of Stock' },
  { value: 'price_discrepancy', label: 'Price Discrepancy' },
  { value: 'delivery_delay', label: 'Delivery Delay' },
  { value: 'customer_unavailable', label: 'Customer Unavailable' },
  { value: 'payment_issues', label: 'Payment Issues' },
  { value: 'duplicate_order', label: 'Duplicate Order' },
  { value: 'other', label: 'Other' },
];

const getStatusText = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.label : `Status ${status}`;
};

const getStatusColor = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : '#9e9e9e';
};

const generationSteps = [
  {
    label: 'Generate Invoice',
    description: 'Creating invoice PDF for the order',
    icon: <Receipt />,
  },
  {
    label: 'Complete',
    description: 'Both invoice and QR code are ready',
    icon: <CheckCircle />,
  },
];

const OrderManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const qrPrintRef = useRef<HTMLDivElement>(null);
  const combinedPrintRef = useRef<HTMLDivElement>(null);
  // Scroll ref for table
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
  const [mobileOpen, setMobileOpen] = useState(false);
  const orderPersist = (() => { try { return JSON.parse(localStorage.getItem('order_management_state') || '{}'); } catch { return {}; } })();
  const [searchStr, setSearchStr] = useState(orderPersist.searchStr || '');
  const [mainTabValue, setMainTabValue] = useState(Number(orderPersist.mainTabValue || 0));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [viewCommentsOpen, setViewCommentsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletableComments, setDeletableComments] = useState<OrderComment[]>([]);
  const [deleteAllCommentsDialogOpen, setDeleteAllCommentsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [commentText, setCommentText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  useEffect(() => { localStorage.setItem('order_management_state', JSON.stringify({ mainTabValue, searchStr })); }, [mainTabValue, searchStr]);
  const [generatedQR, setGeneratedQR] = useState<{ pickupCode: string; qrCode: string } | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedCancelReason, setSelectedCancelReason] = useState('customer_request');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelType, setCancelType] = useState<'admin' | 'user'>('admin');
  // Where a refund should land when the order was paid online (not COD, not
  // Pay Later — those two always have a fixed destination and skip this
  // choice entirely). Admin picks explicitly each time.
  //
  // FIX: this used to default to 'wallet' — since "Wallet" is also the
  // first/top radio option, a card/online order got silently credited to
  // Wallet unless the admin noticed and actively switched it to "Original
  // Payment Method". Defaulting to 'original' instead means a card/online
  // refund goes back the way it was paid unless the admin deliberately
  // diverts it to Wallet — the safer default for money that was actually
  // charged on a card/gateway.
  const [refundDestination, setRefundDestination] = useState<'wallet' | 'original'>('original');

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [deliveryAgentData, setDeliveryAgentData] = useState<any>(null);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [deliveryInfoData, setDeliveryInfoData] = useState<any>(null);
  const [isAutoUpdating, setIsAutoUpdating] = useState(false);

  const [combinedDialogOpen, setCombinedDialogOpen] = useState(false);
  const [isGeneratingCombined, setIsGeneratingCombined] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [combinedResult, setCombinedResult] = useState<GenerateCombinedResponse | null>(null);
  const [combinedQrImage, setCombinedQrImage] = useState<string | null>(null);
  const HIDE_AGENT_STATUSES = [7, 21, 22];
  // Once an order reaches "Pickup Pending" (3) the package has physically
  // left the warehouse with the delivery agent, so admin can no longer
  // cancel it from here — matches the user site, where Cancel Order also
  // disappears once the delivery agent has picked the order up.
  const CANCELLABLE_MAX_STATUS = 2;
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [paymentFilter, setPaymentFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [cancelledPage, setCancelledPage] = useState(1);
  const [refundPage, setRefundPage] = useState(1);
  const rowsPerPage = 10;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  const {
    data,
    isLoading,
    error,
    isError,
    refetch: refetchOrders,
  } = useListOrdersQuery(
    {
      ...(warehouseFilter !== '' && warehouseFilter !== undefined ? { warehouse_id: warehouseFilter } : {}),
      _t: lastUpdateTime,
    },
    {
      pollingInterval: 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const { data: warehousesData } = useListWarehousesQuery();
  const warehouses = warehousesData?.warehouses || [];

  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const [deleteOrder, { isLoading: isDeleting }] = useDeleteOrderMutation();
  const [deleteComment, { isLoading: isDeletingComment }] = useDeleteCommentMutation();
  const [generateQR, { isLoading: isGeneratingQR }] = useGenerateQRMutation();
  const [generateInvoice, { isLoading: isGeneratingInvoice }] = useGenerateInvoiceMutation();
  const [generateCombined] = useGenerateCombinedMutation();
  const [triggerGetQrImage, { isLoading: isLoadingQrImage }] = useLazyGetQrImageQuery();
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();

  const orders = useMemo(() => {
    if (!data?.orders) return [];
    return [...data.orders].sort((a, b) => {
      const dateA = new Date(a.order_date).getTime() || 0;
      const dateB = new Date(b.order_date).getTime() || 0;
      return dateB - dateA;
    });
  }, [data?.orders]);

  const totalRecords = orders.length;

  const handleAutoUpdateToProcessing = async () => {
    if (!selectedOrder) return;
    if (selectedOrder.status === 0 || selectedOrder.status === 1) {
      try {
        await updateOrderStatus({
          order_id: selectedOrder.id,
          status: 2,
          comment: 'Status auto-updated to Processing after document action',
        }).unwrap();
        setSelectedOrder(prev => prev ? { ...prev, status: 2 } : prev);
        refetchOrders();
        setLastUpdateTime(Date.now());
        setSnackbar({ open: true, message: 'Order status updated to Processing', severity: 'success' });
      } catch (err) {
        console.error('Failed to auto-update status:', err);
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const getOrderPaymentMethod = (order: Order): string => {
    if (order.cash_on_delivery === 1) return 'cash_on_delivery';
    else if (order.pay_later === 1) return 'pay_later';
    else return 'online_payment';
  };

  const getOrderWarehouseName = (order: Order): string => {
    if ((order as any).warehouse_name) return (order as any).warehouse_name;
    if (order.items && order.items.length > 0) {
      const firstItem = order.items[0];
      if (firstItem.warehouse_name) return firstItem.warehouse_name;
      if (firstItem.warehouse && firstItem.warehouse.name) return firstItem.warehouse.name;
    }
    if ((order as any).delivery_agent && (order as any).delivery_agent.warehouse_name) {
      return (order as any).delivery_agent.warehouse_name;
    }
    return '-';
  };

  const getDeliveryType = (order: Order | null): string => {
    if (!order || !(order as any).delivery_proof) return 'Not Delivered Yet';
    const deliveryProof = (order as any).delivery_proof;
    if (deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0) return 'Normal Delivery';
    if (deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0) return 'Open Box Delivery';
    if (deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0) return 'Partial Delivery';
    return 'Not Delivered Yet';
  };

  const getDeliveryTypeColor = (deliveryType: string): string => {
    switch (deliveryType) {
      case 'Normal Delivery': return '#4caf50';
      case 'Open Box Delivery': return '#2196f3';
      case 'Partial Delivery': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getAssignmentId = (order: Order | null): string => {
    if (!order || !(order as any).delivery_proof) return '-';
    const deliveryProof = (order as any).delivery_proof;
    if (deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0)
      return deliveryProof.normal_delivery[0].assignment_id || '-';
    if (deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0)
      return deliveryProof.openbox_delivery[0].assignment_id || '-';
    if (deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0)
      return deliveryProof.partial_delivery[0].assignment_id || '-';
    return '-';
  };

  const filteredOrders = orders.filter((order: Order) => {
    if (!searchStr && statusFilter.length === 0 && paymentFilter.length === 0) return true;
    const search = searchStr.toLowerCase();
    const matchesSearch =
      !searchStr ||
      order.id.toString().includes(search) ||
      (order.user_name && order.user_name.toLowerCase().includes(search)) ||
      order.user_id?.toString().includes(search) ||
      getOrderWarehouseName(order).toLowerCase().includes(search);
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(order.status);
    const orderPaymentMethod = getOrderPaymentMethod(order);
    const matchesPayment = paymentFilter.length === 0 || paymentFilter.includes(orderPaymentMethod);
    return matchesSearch && matchesStatus && matchesPayment;
  });

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Scroll handlers for table
  const handleScrollLeft = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
    setCancelledPage(1);
    setRefundPage(1);
  };

  const handleStatusFilterChange = (event: any) => {
    const { target: { value } } = event;
    setStatusFilter(typeof value === 'string' ? value.split(',').map(Number) : value);
    setPage(1);
  };

  const handleWarehouseFilterChange = (event: any) => {
    const value = event.target.value;
    setWarehouseFilter(value === '' ? '' : Number(value));
    setPage(1);
  };

  const handlePaymentFilterChange = (event: any) => {
    const { target: { value } } = event;
    setPaymentFilter(typeof value === 'string' ? value.split(',') : value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchStr('');
    setStatusFilter([]);
    setWarehouseFilter('');
    setPaymentFilter([]);
    setPage(1);
  };

  const handleToggleRow = (orderId: number) => {
    setExpandedRows(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const handleViewOrder = async (order: Order) => {
    setSelectedOrder(order);
    setDetailsTabValue(0);
    setIsLoadingDetails(true);
    setCustomerData(null);
    setDeliveryAgentData(null);
    setWarehouseData(null);
    setDeliveryInfoData(null);

    setTimeout(() => {
      setCustomerData({ name: order.user_name, userId: order.user_id });

      const agent = (order as any).delivery_agent || {};
      let agentName = 'Not assigned';
      if (order.delivery_agent_name) agentName = order.delivery_agent_name;
      else if (agent.first_name || agent.last_name) agentName = `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
      else if (agent.firstName || agent.lastName) agentName = `${agent.firstName || ''} ${agent.lastName || ''}`.trim();
      else if (agent.user_name) agentName = agent.user_name;
      else if (agent.id) agentName = `Agent ${agent.id}`;

      setDeliveryAgentData({
        name: agentName,
        contact: agent.contact || agent.mobile || agent.phone || null,
        email: agent.email || null,
        id: agent.id || null,
      });

      setWarehouseData({ name: getOrderWarehouseName(order), orderDate: order.order_date });

      const deliveryProof = (order as any).delivery_proof;
      setDeliveryInfoData({
        deliveryType: getDeliveryType(order),
        assignmentId: getAssignmentId(order),
        deliveryProof: deliveryProof,
      });

      setIsLoadingDetails(false);
      setShowDetails(true);
    }, 100);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedOrder(null);
    setIsLoadingDetails(false);
    setCustomerData(null);
    setDeliveryAgentData(null);
    setWarehouseData(null);
    setDeliveryInfoData(null);
  };

  const handleStatusUpdateClick = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.status);
    setCommentText('');
    setStatusDialogOpen(true);
  };

  const handleCancelOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setCancelType('admin');
    setSelectedCancelReason('customer_request');
    setCustomCancelReason('');
    setRefundDestination('original');
    setCancelDialogOpen(true);
  };

  const handleDeleteClick = (order: Order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAllCommentsClick = (order: Order) => {
    setSelectedOrder(order);
    const deletable = (order.comments || []).filter((c) => {
      const s = Number((c as any).status);
      return !Number.isNaN(s) && requiresComment(s);
    });
    setDeletableComments(deletable);
    setDeleteAllCommentsDialogOpen(true);
  };

  const handleConfirmDeleteAllComments = async () => {
    if (!selectedOrder) return;
    if (!deletableComments || deletableComments.length === 0) {
      setSnackbar({ open: true, message: 'No comments eligible for deletion (only statuses 5-17 can be deleted).', severity: 'info' });
      setDeleteAllCommentsDialogOpen(false);
      return;
    }
    try {
      const promiseList = deletableComments.map((comment) =>
        deleteComment({ comment_id: comment.id!, order_id: selectedOrder.id })
          .unwrap()
          .then(
            (res) => ({ status: 'fulfilled', res, commentId: comment.id }),
            (err) => ({ status: 'rejected', err, commentId: comment.id })
          )
      );
      const results = await Promise.all(promiseList);
      const failed = results.filter((r) => (r as any).status === 'rejected') as any[];
      const succeeded = results.filter((r) => (r as any).status === 'fulfilled') as any[];

      if (failed.length === 0) {
        setSnackbar({ open: true, message: `Deleted ${succeeded.length} comment(s) successfully.`, severity: 'success' });
      } else if (succeeded.length === 0) {
        const messages = failed.map((f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`);
        setSnackbar({ open: true, message: messages.join(' | '), severity: 'error' });
      } else {
        const failMsgs = failed.map((f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`);
        setSnackbar({ open: true, message: `Deleted ${succeeded.length} comment(s). Failed: ${failMsgs.join(' | ')}`, severity: 'warning' });
      }
      setDeleteAllCommentsDialogOpen(false);
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete all comments:', err);
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting comments', severity: 'error' });
      setDeleteAllCommentsDialogOpen(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    try {
      const result = await deleteOrder({ order_id: selectedOrder.id }).unwrap();
      setSnackbar({ open: true, message: result?.message || 'Order deleted successfully!', severity: 'success' });
      setDeleteDialogOpen(false);
      if (showDetails) { setShowDetails(false); setSelectedOrder(null); }
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete order:', err);
      setSnackbar({ open: true, message: err?.data?.message || 'Error deleting order', severity: 'error' });
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    try {
      const orderType = determineOrderType(selectedOrder);
      let finalCancelReason = '';
      if (selectedCancelReason === 'other') {
        finalCancelReason = customCancelReason.trim();
      } else {
        const reasonOption = CANCEL_REASON_OPTIONS.find(opt => opt.value === selectedCancelReason);
        finalCancelReason = reasonOption ? reasonOption.label : selectedCancelReason;
      }
      if (cancelType === 'admin') finalCancelReason = `Admin Cancelled: ${finalCancelReason}`;
      else finalCancelReason = `User Cancelled: ${finalCancelReason}`;

      // COD and Pay Later orders always have a fixed refund destination on
      // the backend (wallet / pay-later balance respectively) — the
      // wallet-vs-original choice only applies to online-paid orders, so we
      // only send refund_mode in that case.
      const isOnlinePayment = selectedOrder.cash_on_delivery !== 1 && selectedOrder.pay_later !== 1;

      const result = await cancelOrder({
        order_id: selectedOrder.id,
        order_type: orderType as 'regular' | 'guest',
        cancel_reason: finalCancelReason,
        ...(isOnlinePayment ? { refund_mode: refundDestination } : {}),
      }).unwrap();

      setSnackbar({ open: true, message: result.message || 'Order cancelled successfully!', severity: 'success' });
      setCancelDialogOpen(false);
      const updatedOrder = { ...selectedOrder, status: result.status_code || 12 };
      setSelectedOrder(updatedOrder);

      // Tell the admin exactly what happened to the refund. Prefer the
      // backend's own `refund_mode` string (the source of truth for what
      // was actually persisted to cancel_items) and only fall back to
      // guessing from the order flags if an older API response doesn't
      // include it. Shown for every cancellation, including COD (where the
      // message will read "COD - No refund"), so admin never has to guess.
      const refundOutcome = result.refund_mode
        || (selectedOrder.cash_on_delivery === 1
          ? 'COD - No refund'
          : selectedOrder.pay_later === 1
            ? 'Credited to Pay Later balance'
            : (refundDestination === 'original'
              ? ((selectedOrder as any)?.wallet === 1 ? 'Credited to Voto Credits' : `Refunded to Original Payment Method (${(selectedOrder as any)?.payment_method || 'Online Payment'})`)
              : 'Credited to Voto Credits (Wallet)'));

      setTimeout(() => {
        setSnackbar({
          open: true,
          message: `Refund mode: ${refundOutcome}`,
          severity: 'info',
        });
      }, 1000);
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to cancel order:', err);
      setSnackbar({ open: true, message: err?.data?.message || 'Error cancelling order', severity: 'error' });
    }
  };

  const requiresComment = (status: number) => {
    return [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 23].includes(status);
  };

  const handleStatusUpdate = async (comment?: string) => {
    if (!selectedOrder) return;
    try {
      const updateData: any = { order_id: selectedOrder.id, status: selectedStatus };
      if (comment && comment.trim()) updateData.comment = comment;
      if (requiresComment(selectedStatus) && (!comment || !comment.trim())) {
        const statusOption = ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus);
        updateData.comment = `Status updated to: ${statusOption?.label || getStatusText(selectedStatus)}`;
      }
      const updatedOrder = { ...selectedOrder, status: selectedStatus };
      setSelectedOrder(updatedOrder);
      const result = await updateOrderStatus(updateData).unwrap();
      setSnackbar({ open: true, message: result?.message || 'Order status updated successfully!', severity: 'success' });
      setStatusDialogOpen(false);
      setCommentDialogOpen(false);
      setCommentText('');
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to update order status:', err);
      setSnackbar({ open: true, message: err?.data?.message || 'Error updating order status', severity: 'error' });
      refetchOrders();
      setLastUpdateTime(Date.now());
    }
  };

  const handleCommentSubmit = () => {
    if (requiresComment(selectedStatus) && !commentText.trim()) {
      setSnackbar({ open: true, message: 'Comment is required for this status update', severity: 'warning' });
      return;
    }
    handleStatusUpdate(commentText);
    setCommentText('');
  };

  const handleViewComments = (order: Order) => {
    setSelectedOrder(order);
    setViewCommentsOpen(true);
  };

  const resolveStatusOption = (status: number | string | undefined) => {
    if (status === undefined || status === null) return null;
    const num = Number(status);
    if (Number.isNaN(num)) return null;
    return ORDER_STATUS_OPTIONS.find((opt) => opt.value === num) || null;
  };

  const inferStatusFromCommentText = (text?: string) => {
    if (!text) return null;
    const normalized = String(text).toLowerCase();
    for (const opt of ORDER_STATUS_OPTIONS) {
      if (!opt || !opt.label) continue;
      if (normalized.includes(String(opt.label).toLowerCase())) return opt;
    }
    return null;
  };

  const CommentItem = ({ comment }: { comment: OrderComment }) => {
    const statusLabelFromApi = (comment as any).status_label || (comment as any).status_text || null;
    const inferredOption = inferStatusFromCommentText(comment.comment);
    const fromStatusField = resolveStatusOption((comment as any).status);

    let chipLabel: string | undefined;
    let chipColor = '#9e9e9e';

    if (statusLabelFromApi) {
      chipLabel = String(statusLabelFromApi);
      const matched = ORDER_STATUS_OPTIONS.find((o) => String(o.label).toLowerCase() === String(statusLabelFromApi).toLowerCase());
      if (matched) chipColor = matched.color;
    } else if (inferredOption) {
      chipLabel = inferredOption.label;
      chipColor = inferredOption.color;
    } else if (fromStatusField) {
      chipLabel = fromStatusField.label;
      chipColor = fromStatusField.color;
    } else if ((comment as any).status !== undefined && (comment as any).status !== null) {
      chipLabel = `Status ${(comment as any).status}`;
    } else {
      chipLabel = undefined;
    }

    return (
      <Box sx={{ mb: 2, p: 2, backgroundColor: comment.user_type === 'admin' ? '#f8f9ff' : '#f0f8ff', borderRadius: 2, border: `1px solid ${comment.user_type === 'admin' ? '#e3f2fd' : '#e8f5e8'}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: comment.user_type === 'admin' ? '#1976d2' : '#4caf50', width: 32, height: 32 }}>
              {comment.user_type === 'admin' ? 'A' : 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {comment.user_type === 'admin' ? 'Admin' : `User: ${comment.user_name || comment.user_id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {comment.created_at ? formatDateTime(comment.created_at) : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {chipLabel && (
              <Chip label={chipLabel} size="small" sx={{ backgroundColor: chipColor, color: 'white', height: 20 }} />
            )}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ pl: 6, fontStyle: 'italic', color: 'text.primary' }}>
          "{comment.comment}"
        </Typography>
      </Box>
    );
  };

  const CommentsList = ({ orderId, maxHeight = 400, showDeleteAllButton = false }: { orderId: number; maxHeight?: number; showDeleteAllButton?: boolean }) => {
    const orderComments = getCommentsForOrder(orderId);
    const order = orders.find((o) => o.id === orderId);

    if (orderComments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No comments available for this order.
        </Typography>
      );
    }

    return (
      <Box>
        {showDeleteAllButton && orderComments.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteSweep />} onClick={() => order && handleDeleteAllCommentsClick(order)} disabled={isDeletingComment}>
              Delete All comments ({orderComments.length})
            </Button>
          </Box>
        )}
        <Box sx={{ maxHeight, overflowY: 'auto' }}>
          {orderComments.slice().sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()).map((comment, index) => (
            <CommentItem key={comment.id || index} comment={comment} />
          ))}
        </Box>
      </Box>
    );
  };

  const renderAgentInfo = (loading: boolean, data: any) => {
    if (loading) {
      return (
        <Box>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      );
    }
    if (!data || !data.name) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff6a00', textTransform: 'uppercase' }}>{data.name}</Typography>
        {data.contact && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{data.contact}</Typography>}
        {data.email && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '11px' }}>{data.email}</Typography>}
        {!data.contact && !data.email && data.id && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>ID: {data.id}</Typography>}
      </Box>
    );
  };

  const getOrderType = (order: any) => {
    try {
      const inferred = determineOrderType(order);
      return inferred === 'guest' ? 'guest' : 'regular';
    } catch (e) {
      return 'regular';
    }
  };

  const handleGenerateCombined = async (order: Order) => {
    if (!order) return;
    setIsGeneratingCombined(true);
    setSelectedOrder(order);
    setGenerationStep(0);
    setCombinedResult(null);
    setCombinedQrImage(null);
    setCombinedDialogOpen(true);

    try {
      const orderType = getOrderType(order);
      setGenerationStep(1);
      const result = await generateCombined({
        order_id: order.id,
        user_id: order.user_id,
        order_type: orderType as 'regular' | 'guest',
      }).unwrap();
      setGenerationStep(2);

      if (result.qr) {
        try {
          const imageResult = await triggerGetQrImage({ order_id: order.id, order_type: orderType as 'regular' | 'guest' }).unwrap();
          if (imageResult) {
            const imageUrl = URL.createObjectURL(imageResult);
            setCombinedQrImage(imageUrl);
          }
        } catch (imgErr) {
          console.warn('QR image fetch failed:', imgErr);
        }
      }

      setGenerationStep(3);
      setCombinedResult(result);
      setSnackbar({ open: true, message: 'Invoice and QR generated successfully!', severity: 'success' });
    } catch (err: any) {
      console.error('Failed to generate combined:', err);
      setSnackbar({ open: true, message: err?.data?.message || 'Error generating invoice and QR', severity: 'error' });
      setGenerationStep(0);
    } finally {
      setIsGeneratingCombined(false);
    }
  };

  const handleDownloadCombinedInvoice = async () => {
    if (!combinedResult?.invoice?.download_url) {
      setSnackbar({ open: true, message: 'No invoice available', severity: 'error' });
      return;
    }
    await handleAutoUpdateToProcessing();
    try {
      const link = document.createElement('a');
      link.href = combinedResult.invoice.download_url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = `invoice_${selectedOrder?.id}_${combinedResult.invoice.invoice_no}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'Invoice download started', severity: 'success' });
    } catch (err: any) {
      console.error('Failed to download invoice:', err);
      setSnackbar({ open: true, message: 'Failed to download invoice', severity: 'error' });
    }
  };

  const handleDownloadCombinedQR = async () => {
    if (!combinedResult?.qr) {
      setSnackbar({ open: true, message: 'No QR code available', severity: 'error' });
      return;
    }
    await handleAutoUpdateToProcessing();
    try {
      if (combinedQrImage) {
        const link = document.createElement('a');
        link.href = combinedQrImage;
        link.download = `order_${selectedOrder?.id}_qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (combinedResult.qr.qr_code) {
        const cleanBase64 = combinedResult.qr.qr_code.replace(/\s/g, '');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${cleanBase64}`;
        link.download = `order_${selectedOrder?.id}_qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbar({ open: true, message: 'No QR image data available', severity: 'warning' });
        return;
      }
      setSnackbar({ open: true, message: 'QR code downloaded successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Download Failed:', error);
      setSnackbar({ open: true, message: `Failed to download QR code: ${error?.message || 'Unknown error'}`, severity: 'error' });
    }
  };

  const handlePrintCombined = async () => {
    if (isAutoUpdating) return;
    setIsAutoUpdating(true);
    try {
      if (selectedOrder && (selectedOrder.status === 0 || selectedOrder.status === 1)) {
        try {
          await updateOrderStatus({ order_id: selectedOrder.id, status: 2, comment: 'Status auto-updated to Processing after print' }).unwrap();
          setSelectedOrder(prev => prev ? { ...prev, status: 2 } : prev);
          refetchOrders();
          setLastUpdateTime(Date.now());
          setSnackbar({ open: true, message: 'Order status updated to Processing', severity: 'success' });
        } catch (err) {
          console.error('Failed to auto-update status:', err);
        }
      }

      const qrImageSource = combinedQrImage || (combinedResult?.qr?.qr_code ? `data:image/png;base64,${combinedResult.qr.qr_code}` : '');
      const invoiceUrl = combinedResult?.invoice?.download_url || '';
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setSnackbar({ open: true, message: 'Pop-up blocked. Please allow pop-ups to print.', severity: 'warning' });
        return;
      }

      printWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
<title>Order #${selectedOrder?.id} - Invoice & QR</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 100%; font-family: Arial, sans-serif; }
  .invoice-page { width: 210mm; height: 297mm; overflow: hidden; position: relative; page-break-after: always; page-break-inside: avoid; }
  .invoice-page iframe { position: absolute; top: -42px; left: 0; width: 100%; height: calc(100% + 42px); border: none; }
  .qr-page { width: 210mm; height: 297mm; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 30mm 20mm; page-break-before: always; page-break-inside: avoid; position: relative; }
  .qr-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; width: 100%; max-width: 500px; }
  .qr-header h1 { font-size: 24px; margin-bottom: 5px; }
  .qr-header p { font-size: 14px; color: #666; }
  .qr-box { border: 3px solid #333; border-radius: 12px; padding: 30px; text-align: center; max-width: 400px; width: 100%; background: white; }
  .qr-box img { width: 240px; height: 240px; object-fit: contain; margin-bottom: 20px; display: block; margin-left: auto; margin-right: auto; }
  .pickup-label { font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; }
  .pickup-code { font-size: 32px; font-weight: bold; letter-spacing: 5px; font-family: 'Courier New', monospace; background: #f5f5f5; padding: 12px 20px; border-radius: 8px; border: 1px solid #ddd; }
  .order-meta { margin-top: 20px; font-size: 13px; color: #555; }
  .order-meta p { margin: 4px 0; }
  .footer { position: absolute; bottom: 15mm; font-size: 11px; color: #aaa; text-align: center; width: 100%; }
  @media print { @page { size: A4; margin: 0; } body { margin: 0; } }
</style>
</head>
<body>
<div class="invoice-page">
  <iframe src="${invoiceUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH" id="invoiceFrame"></iframe>
</div>
<div class="qr-page">
  <div class="qr-header">
    <h1>QR CODE FOR PICKUP</h1>
    <p>Order #${selectedOrder?.id} | ${selectedOrder?.user_name}</p>
  </div>
  <div class="qr-box">
    ${qrImageSource ? `<img src="${qrImageSource}" alt="QR Code" />` : `<div style="width:240px;height:240px;background:#eee;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;border-radius:8px;font-size:13px;color:#999;">QR Not Available</div>`}
    <div class="pickup-label">Pickup Code</div>
    <div class="pickup-code">${combinedResult?.qr?.pickup_code || 'N/A'}</div>
    <div class="order-meta">
      <p><strong>Customer:</strong> ${selectedOrder?.user_name}</p>
      <p><strong>Total:</strong> AED ${getOrderBalanceAmount(selectedOrder).toFixed(2)}</p>
      <p><strong>Date:</strong> ${selectedOrder?.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : '-'}</p>
    </div>
  </div>
  <div class="footer">Generated on ${new Date().toLocaleString()} | ${window.location.hostname}</div>
</div>
<script>
  document.getElementById('invoiceFrame').onload = function() {
    setTimeout(function() { window.focus(); window.print(); window.onafterprint = function() { window.close(); }; }, 1200);
  };
</script>
</body>
</html>`);
      printWindow.document.close();
    } finally {
      setIsAutoUpdating(false);
    }
  };

  const handleGenerateQR = async (order: Order) => {
    try {
      const fullOrder = data?.orders?.find((o) => o.id === order.id) || order;
      const orderType = getOrderType(fullOrder);
      setSnackbar({ open: true, message: `Generating QR Code for ${orderType} order...`, severity: 'info' });

      const result = await generateQR({ order_id: order.id, order_type: orderType as 'regular' | 'guest' }).unwrap();

      if (result.success) {
        setGeneratedQR({ pickupCode: result.pickup_code, qrCode: result.qr_code });
        try {
          const imageResult = await triggerGetQrImage({ order_id: order.id, order_type: orderType as 'regular' | 'guest' }).unwrap();
          try {
            const maybeBlob = imageResult as unknown as Blob;
            if (maybeBlob && (maybeBlob instanceof Blob || ((maybeBlob as any).size !== undefined && (maybeBlob as any).type !== undefined))) {
              setQrImageUrl(URL.createObjectURL(maybeBlob as Blob));
            } else {
              setQrImageUrl(null);
            }
          } catch (imgInnerErr) {
            setQrImageUrl(null);
          }
        } catch (imgErr: any) {
          setQrImageUrl(null);
        }
        setSelectedOrder(order);
        setQrDialogOpen(true);
        setSnackbar({ open: true, message: `QR Code generated successfully for ${orderType} order!`, severity: 'success' });
      } else {
        const msg = result.message || 'QR generation failed';
        setSnackbar({ open: true, message: msg, severity: msg.toLowerCase().includes('not found') ? 'error' : 'warning' });
      }
    } catch (err: any) {
      console.error('QR Generation Failed:', err);
      const backendMessage = err?.data?.message || err?.message || String(err);
      setSnackbar({
        open: true,
        message: backendMessage.includes('Order not found') ? `Order not found. Check whether this is a guest order and use guest QR endpoint.` : backendMessage,
        severity: 'error',
      });
    }
  };

  const handleOpenInvoice = async (order: Order) => {
    try {
      setSelectedOrder(order);
      setSnackbar({ open: true, message: 'Generating invoice...', severity: 'info' });

      const result = await generateInvoice({ order_id: order.id, user_id: order.user_id }).unwrap();

      if (!result?.error && result?.download_url) {
        if (order.status === 0 || order.status === 1) {
          try {
            await updateOrderStatus({ order_id: order.id, status: 2, comment: 'Status auto-updated to Processing after document action' }).unwrap();
            refetchOrders();
            setLastUpdateTime(Date.now());
          } catch (statusErr) {
            console.error('Failed to auto-update status:', statusErr);
          }
        }
        window.open(result.download_url, '_blank');
        setSnackbar({ open: true, message: 'Invoice opened in new tab.', severity: 'success' });
      } else {
        const msg = result?.message || 'Invoice generation failed';
        setSnackbar({ open: true, message: msg, severity: msg.toLowerCase().includes('not found') ? 'error' : 'warning' });
      }
    } catch (err: any) {
      console.error('Invoice Generation Failed:', err);
      const backendMessage = err?.data?.message || err?.message || String(err);
      setSnackbar({ open: true, message: backendMessage, severity: 'error' });
    }
  };

  const handleDownloadQR = async () => {
    if (!generatedQR || !selectedOrder) {
      setSnackbar({ open: true, message: 'Missing QR data or selected order. Please generate QR first.', severity: 'error' });
      return;
    }
    const orderType = getOrderType(selectedOrder);
    try {
      if (qrImageUrl) {
        const link = document.createElement('a');
        link.href = qrImageUrl;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({ open: true, message: 'QR Code downloaded successfully!', severity: 'success' });
        return;
      }
      if (generatedQR.qrCode) {
        const cleanBase64 = generatedQR.qrCode.replace(/\s/g, '');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${cleanBase64}`;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({ open: true, message: 'QR Code downloaded successfully!', severity: 'success' });
        return;
      }
      const DELIVERY_URL = (process.env.REACT_APP_DELIVERY_ENDPOINT || '').replace(/\/$/, '');
      const apiUrl = `${DELIVERY_URL}/qrgenerater/${orderType}/${selectedOrder.id}/qr/image`;
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`API returned ${response.status}: ${response.statusText}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'QR Code downloaded successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Download Failed:', error);
      setSnackbar({ open: true, message: `Failed to download QR code: ${error?.message || 'Unknown error'}. Please try again.`, severity: 'error' });
    }
  };

  const handlePrintQR = () => {
    if (!qrPrintRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const imageSource = qrImageUrl || `data:image/png;base64,${generatedQR?.qrCode}`;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order QR Code - #${selectedOrder?.id}</title>
          <style>
            body { font-family: Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; padding: 20px; }
            .qr-container { text-align: center; border: 2px solid #000; padding: 30px; border-radius: 10px; }
            .order-info { margin-bottom: 20px; }
            .order-info h2 { margin: 0 0 10px 0; }
            .order-info p { margin: 5px 0; font-size: 14px; }
            .qr-image { margin: 20px 0; }
            .qr-image img { max-width: 300px; width: 100%; background-color: white; }
            .pickup-code { font-size: 24px; font-weight: bold; margin-top: 15px; letter-spacing: 2px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="order-info">
              <h2>Order #${selectedOrder?.id}</h2>
              <p>Customer: ${selectedOrder?.user_name}</p>
              <p>Date: ${selectedOrder?.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : '-'}</p>
              <p><strong>Total: AED ${getOrderBalanceAmount(selectedOrder).toFixed(2)}</strong></p>
            </div>
            <hr />
            <div class="qr-image"><img src="${imageSource}" alt="Order QR Code" /></div>
            <div class="pickup-code"><small>Pickup Code</small><br />${generatedQR?.pickupCode || ''}</div>
          </div>
        </body>
      </html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: string | number | undefined) => {
    const n = Number(amount || 0);
    if (Number.isNaN(n)) return `AED ${amount}`;
    return `AED ${n.toFixed(2)}`;
  };

  // ---------------------------------------------------------------------
  // Cancellation helpers (mirrors the logic used on the user-facing site's
  // view_order page so the backoffice reflects the exact same item count
  // and price detail once an order/item has been cancelled).
  //
  // The backend reliably sends `cancellable_qty` (remaining, non-cancelled
  // quantity) on every item. The explicit `is_cancelled` / `is_partially_cancelled`
  // / `cancelled_qty` flags are not always populated, so we derive the real
  // cancellation state from `cancellable_qty` and treat the explicit flags
  // as an optional override rather than the source of truth.
  // ---------------------------------------------------------------------
  const getItemCancellationInfo = (item: any) => {
    const qty = Number(item.product_quantity || item.quantity || item.qty || 0);
    const cancellable = item.cancellable_qty ?? (qty - Number(item.cancelled_qty || 0));
    const cancellableQty = Number(cancellable ?? qty);
    const derivedCancelledQty = Math.max(qty - cancellableQty, 0);
    const cancelledQty = Number(item.cancelled_qty || derivedCancelledQty || 0);

    const isFullyCancelled = Boolean(item.is_cancelled) || (cancellableQty <= 0 && qty > 0);
    const isPartiallyCancelled = !isFullyCancelled &&
      (Boolean(item.is_partially_cancelled) || (cancelledQty > 0 && cancelledQty < qty));

    return { qty, cancelledQty, isFullyCancelled, isPartiallyCancelled, isCancelled: isFullyCancelled || isPartiallyCancelled };
  };

  // value that should be deducted from the order total because of cancelled
  // quantity — each item's line total is scaled down proportionally to the
  // qty that was cancelled (e.g. cancelling 1 of 2 units deducts half that
  // item's total). Matches getCancelledItemsValue() on the user site.
  const getCancelledItemsValue = (items: any[]) => {
    return (items || []).reduce((acc: number, item: any) => {
      const qty = Number(item.product_quantity || item.quantity || item.qty || 0);
      if (!qty) return acc;
      const { cancelledQty } = getItemCancellationInfo(item);
      if (!cancelledQty) return acc;
      const unitPrice = Number(item.unit_price || item.price || item.unitPrice || 0);
      const lineTotal = Number(item.itemTotal ?? item.item_total ?? (unitPrice * qty));
      return acc + (lineTotal * cancelledQty / qty);
    }, 0);
  };

  // Same per-item math as getCancelledItemsValue, but for a single item
  // instead of summing across a whole order — used by the flat Cancelled
  // Orders list below, one row per cancelled line item.
  const getItemCancelledValue = (item: any): number => {
    const qty = Number(item.product_quantity || item.quantity || item.qty || 0);
    if (!qty) return 0;
    const { cancelledQty } = getItemCancellationInfo(item);
    if (!cancelledQty) return 0;
    const unitPrice = Number(item.unit_price || item.price || item.unitPrice || 0);
    const lineTotal = Number(item.itemTotal ?? item.item_total ?? (unitPrice * qty));
    return lineTotal * cancelledQty / qty;
  };

  // ---------------------------------------------------------------------
  // Cancelled Orders list (dedicated Backoffice view). A flat, one-row-per
  // -cancelled-line-item list across every order, with how (or whether)
  // each was refunded. This is the single list Finance can work off of to
  // process/reconcile refunds, instead of hunting through individual order
  // detail pages one by one.
  // ---------------------------------------------------------------------
  const cancelledLineItems = useMemo(() => {
    const rows: Array<{
      key: string;
      order: Order;
      orderId: number;
      orderDate: string;
      userName: string;
      userId: number | string | null | undefined;
      productName: string;
      cancelledQty: number;
      totalQty: number;
      value: number;
      reason: string;
      cancelledAt: string;
      refundMode: string;
      paymentMethod: string;
      transactionId: string | number | null;
    }> = [];

    orders.forEach((order: Order) => {
      (order.items || []).forEach((item: any) => {
        const info = getItemCancellationInfo(item);
        if (!info.isCancelled) return;
        const paymentMethod = getOrderPaymentMethod(order);
        const isCOD = paymentMethod === 'cash_on_delivery';
        rows.push({
          key: `${order.id}-${item.id}`,
          order,
          orderId: order.id,
          orderDate: order.order_date,
          userName: order.user_name || 'Guest',
          userId: order.user_id,
          productName: item.product_name || item.prod_name || `Product #${item.product_id}`,
          cancelledQty: info.cancelledQty,
          totalQty: info.qty,
          value: getItemCancelledValue(item),
          reason: item.cancel_reason || order.comment || '-',
          cancelledAt: item.cancelled_at || order.order_date,
          // cancel_refund_mode comes straight from cancel_items.refund_mode
          // (set by the backend once the cancel-time refund logic runs).
          // COD orders never had a charge to refund, so there's nothing
          // pending there either — anything else missing a value here is a
          // genuinely unresolved refund that needs manual attention.
          refundMode: item.cancel_refund_mode || (isCOD ? 'COD - No refund due' : 'Pending'),
          paymentMethod,
          // Only ever set for card/online ("Refunded to Original Payment
          // Method") refunds — COD, Pay Later, and Wallet credits are
          // internal, with no gateway transaction to reference. Comes
          // straight from /adminlistOrders' cancel_transaction_id, the
          // same payment_transaction row the Refund Initiated insert in
          // /cancelOrder creates.
          transactionId: item.cancel_transaction_id ?? null,
        });
      });
    });

    return rows.sort((a, b) => new Date(b.cancelledAt).getTime() - new Date(a.cancelledAt).getTime());
  }, [orders]);

  const filteredCancelledItems = useMemo(() => {
    const search = searchStr.toLowerCase();
    if (!search) return cancelledLineItems;
    return cancelledLineItems.filter((row) =>
      row.orderId.toString().includes(search) ||
      row.userName.toLowerCase().includes(search) ||
      row.productName.toLowerCase().includes(search)
    );
  }, [cancelledLineItems, searchStr]);

  const cancelledRowsPerPage = 10;
  const cancelledTotalPages = Math.max(1, Math.ceil(filteredCancelledItems.length / cancelledRowsPerPage));
  const paginatedCancelledItems = filteredCancelledItems.slice(
    (cancelledPage - 1) * cancelledRowsPerPage,
    cancelledPage * cancelledRowsPerPage
  );

  // ---------------------------------------------------------------------
  // Refunds list — the list Finance actually needs: cancelled items where
  // money was really taken from the customer and has to (or already did)
  // come back to them. This is a subset of cancelledLineItems — it drops
  // COD rows (nothing was ever collected, so nothing to refund) so Finance
  // isn't scanning through no-op rows to find the ones that matter.
  // ---------------------------------------------------------------------
  const refundableItems = useMemo(() => {
    return cancelledLineItems.filter((row) => {
      const mode = (row.refundMode || '').toLowerCase();
      // Exclude COD (nothing was ever collected) and explicit "no refund"
      // records (e.g. order was cancelled while payment was still pending).
      // Everything else — Credited to Wallet, Credited to Pay Later balance,
      // or a still-"Pending" row awaiting reconciliation — belongs here.
      return !mode.includes('cod') && mode !== 'no refund';
    });
  }, [cancelledLineItems]);

  const filteredRefundableItems = useMemo(() => {
    const search = searchStr.toLowerCase();
    if (!search) return refundableItems;
    return refundableItems.filter((row) =>
      row.orderId.toString().includes(search) ||
      row.userName.toLowerCase().includes(search) ||
      row.productName.toLowerCase().includes(search)
    );
  }, [refundableItems, searchStr]);

  const refundRowsPerPage = 10;
  const refundTotalPages = Math.max(1, Math.ceil(filteredRefundableItems.length / refundRowsPerPage));
  const paginatedRefundItems = filteredRefundableItems.slice(
    (refundPage - 1) * refundRowsPerPage,
    refundPage * refundRowsPerPage
  );

  const refundSummary = useMemo(() => {
    const completed = refundableItems.filter((row) => (row.refundMode || '').toLowerCase().includes('credited'));
    const pending = refundableItems.filter((row) => !(row.refundMode || '').toLowerCase().includes('credited'));
    return {
      completedCount: completed.length,
      pendingCount: pending.length,
      completedValue: completed.reduce((sum, row) => sum + row.value, 0),
      pendingValue: pending.reduce((sum, row) => sum + row.value, 0),
    };
  }, [refundableItems]);

  // Number of line items still active on the order (excludes items that have
  // been fully cancelled), matching the count shown on the user-facing site.
  // Falls back to the raw item/order count when no per-item cancellation
  // data is available.
  const getVisibleItemsCount = (order: Order | null | undefined): number => {
    const items = (order as any)?.items || [];
    if (items.length === 0) return (order as any)?.order_items_count ?? 0;
    return items.filter((item: any) => !getItemCancellationInfo(item).isFullyCancelled).length;
  };

  // Order total after deducting the value of any cancelled item quantity —
  // the "Balance Amount" the customer actually owes/paid, matching the
  // user-facing site's order list and order detail totals.
  const getOrderBalanceAmount = (order: Order | null | undefined): number => {
    const cancelledValue = getCancelledItemsValue((order as any)?.items || []);
    return Math.max(Number((order as any)?.total_order_amount || 0) - cancelledValue, 0);
  };

  const getStatusChip = (status: number) => {
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === status);
    const label = statusOption?.label || `Status ${status}`;
    return (
      <Tooltip title={label} arrow placement="top">
        <Chip
          label={label}
          size="small"
          sx={{
            backgroundColor: statusOption?.color || '#9e9e9e',
            color: 'white',
            fontWeight: 600,
            fontSize: '12px',
            maxWidth: '120px',
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
      </Tooltip>
    );
  };

  const getCommentsForOrder = (orderId: number): OrderComment[] => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return [];
    return order.comments || [];
  };

  const getCommentsForStatus = (orderId: number, status: number): OrderComment[] => {
    return getCommentsForOrder(orderId).filter((comment) => comment.status === status);
  };

  const hasCommentsForStatus = (orderId: number, status: number) => getCommentsForStatus(orderId, status).length > 0;
  const hasAnyComments = (orderId: number) => getCommentsForOrder(orderId).length > 0;

  const getProductImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath || imagePath.trim() === '') return '';
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    if (cleanPath.startsWith('http')) return cleanPath;
    return `${BASE_URL}/images/${cleanPath}`;
  };

  const getDeliveryProofImageUrl = (imagePath: string): string => {
    if (!imagePath || imagePath.trim() === '') return '';
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    if (cleanPath.startsWith('http')) return cleanPath;
    if (cleanPath.includes('uploads/')) return `${BASE_URL}/${cleanPath}`;
    if (cleanPath.includes('images/')) return `${BASE_URL}/${cleanPath}`;
    if (cleanPath.includes('backend/')) return `${BASE_URL}/${cleanPath}`;
    return `${BASE_URL}/images/${cleanPath}`;
  };

  const renderOrderItems = (order: Order | null | undefined) => {
    if (!order) return null;
    const items = order.items || [];
    if (items.length === 0) {
      return <Typography variant="body2" color="text.secondary">No items found for this order.</Typography>;
    }

    return (
      <Box>
        {items.map((item: any, idx: number) => {
          const productName = item.product_name || item.name || `Product ${item.product_id || ''}`;
          const productImage = item.product_image || item.product_main_image || null;
          const imageUrl = getProductImageUrl(productImage);

          const qty = item.product_quantity || item.quantity || item.qty || 0;
          const unitPrice = item.unit_price || item.price || item.unitPrice || '0';
          const discount = item.discount || item.discountAmt || item.discount_amount || '0';
          const productprice = item.product_price || item.price || item.product_price || '0';

          const discountPercent = Number(discount);
          const discountedUnitPrice = discountPercent > 0
            ? Number(productprice) - (Number(unitPrice) * discountPercent / 100)
            : Number(unitPrice);
          const itemTotal = item.itemTotal || item.item_total || (discountedUnitPrice * Number(qty)).toFixed(2);

          const warehouseId = item.warehouse_id ?? item.warehouse?.id ?? item.warehouseId;
          const warehouseName =
            item.warehouse_name ??
            (item.warehouse && (item.warehouse.name || item.warehouse.warehouse_name)) ??
            (item.warehouse?.name) ??
            '-';
          const warehouseExtra = item.warehouse_extra_cost ?? item.warehouse?.extra_cost ?? '0';
          const openBoxCharge = item.open_box_charge ?? '0';

          // Per-product cancellation (set by backend when this specific item
          // was cancelled out of a multi-product order via /cancelOrder items[]).
          // Derived the same way as the user-facing site (via cancellable_qty)
          // so cancelled items/price details always show correctly here too.
          const { cancelledQty, isFullyCancelled, isPartiallyCancelled, isCancelled } = getItemCancellationInfo(item);

          return (
            <Box
              key={item.id || idx}
              sx={{
                display: 'flex', gap: 2, alignItems: 'flex-start', p: 2, borderRadius: 1,
                border: isCancelled ? '1px solid #ffcdd2' : '1px solid #e9ecef', mb: 2,
                backgroundColor: isFullyCancelled ? '#fff5f5' : '#fff',
                opacity: isFullyCancelled ? 0.85 : 1,
              }}
            >
              <Box
                sx={{ width: 80, height: 80, flexShrink: 0, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', border: '1px solid #f1f1f1', position: 'relative' }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={productName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', filter: isFullyCancelled ? 'grayscale(1)' : 'none' }}
                    onError={(e) => { const img = e.target as HTMLImageElement; img.style.display = 'none'; }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <ImageIcon sx={{ color: '#ccc', fontSize: 32 }} />
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>No Image</Typography>
                  </Box>
                )}
                {imageUrl && (
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px', px: 0.5, py: 0.25, textAlign: 'center' }}>
                    ID: {item.product_id}
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{productName}</Typography>
                  {isFullyCancelled && (
                    <Chip
                      size="small"
                      icon={<Cancel sx={{ fontSize: '14px !important' }} />}
                      label="Cancelled"
                      sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, height: 22 }}
                    />
                  )}
                  {isPartiallyCancelled && (
                    <Chip
                      size="small"
                      icon={<Cancel sx={{ fontSize: '14px !important' }} />}
                      label={`Partially Cancelled (${cancelledQty}/${qty})`}
                      sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, height: 22 }}
                    />
                  )}
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Item Total</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={isFullyCancelled ? 'text.secondary' : 'primary'}
                    sx={isFullyCancelled ? { textDecoration: 'line-through' } : undefined}
                  >
                    {formatCurrency((Number(unitPrice) * Number(qty)).toFixed(2))}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Quantity</Typography>
                    <Typography variant="body2" fontWeight="bold">{qty} × {formatCurrency(unitPrice)}</Typography>
                  </Box>
                  {isPartiallyCancelled && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Cancelled</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error">{cancelledQty} of {qty}</Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>Warehouse Details</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Warehouse</Typography>
                      <Typography variant="body2">{warehouseName} (ID: {warehouseId || '-'})</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Extra Cost</Typography>
                      <Typography variant="body2">{formatCurrency(warehouseExtra)}</Typography>
                    </Box>
                  </Box>   
                </Box>

                {isCancelled && item.cancel_reason && (
                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, backgroundColor: '#fff5f5', border: '1px solid #ffcdd2' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#c62828' }}>Cancellation Reason</Typography>
                    <Typography variant="body2" color="text.secondary">{item.cancel_reason}</Typography>
                    {item.cancelled_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Cancelled on {formatDate(item.cancelled_at)}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Per-product Refund Mode — a single order can have items
                    refunded differently (e.g. one product back to the
                    original card, another to Wallet), because refund_mode
                    is decided per cancelled line item on the backend
                    (cancel_items.refund_mode), not once for the whole
                    order. Showing it here (not just the order-level
                    Payment Summary card) is what makes that mix visible. */}
                {isCancelled && item.cancel_refund_mode && (
                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, backgroundColor: '#f0f7ff', border: '1px solid #bbdefb' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#1565c0' }}>Refund Mode (this item)</Typography>
                    <Chip
                      size="small"
                      icon={<AccountBalanceWallet sx={{ fontSize: '14px !important' }} />}
                      label={item.cancel_refund_mode}
                      sx={{
                        fontWeight: 600,
                        height: 'auto',
                        bgcolor: String(item.cancel_refund_mode).toLowerCase().includes('wallet') || String(item.cancel_refund_mode).toLowerCase().includes('voto credits')
                          ? '#e3f7ec' : '#fff3e0',
                        color: String(item.cancel_refund_mode).toLowerCase().includes('wallet') || String(item.cancel_refund_mode).toLowerCase().includes('voto credits')
                          ? '#0f7b45' : '#e65100',
                        '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.5, lineHeight: 1.3 },
                      }}
                    />
                    {item.cancel_transaction_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                        Txn ID: {item.cancel_transaction_id}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderDeliveryProofImages = (order: Order | null) => {
    if (!order || !(order as any).delivery_proof) {
      return <Typography variant="body2" color="text.secondary">No delivery proof available.</Typography>;
    }

    const deliveryProof = (order as any).delivery_proof;

    return (
      <Box sx={{ mt: 2 }}>
        {deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4caf50' }}>Normal Delivery Proof</Typography>
            <Grid container spacing={2}>
              {deliveryProof.normal_delivery.map((proof: any, index: number) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Image {index + 1}</Typography>
                    <Box sx={{ width: '100%', height: 200, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', mb: 1, border: '1px solid #e0e0e0' }}>
                      {proof.image ? (
                        <img
                          src={getDeliveryProofImageUrl(proof.image)}
                          alt={`Delivery proof ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            const originalPath = proof.image;
                            const cleanPath = originalPath.startsWith('/') ? originalPath.substring(1) : originalPath;
                            const patterns = [`${BASE_URL}/uploads/${cleanPath}`, `${BASE_URL}/images/${cleanPath}`, `${BASE_URL}/${cleanPath}`, `${BASE_URL}/backend/${cleanPath}`];
                            let currentIndex = patterns.findIndex(pattern => pattern === img.src);
                            let nextIndex = currentIndex + 1;
                            if (nextIndex < patterns.length) { img.src = patterns[nextIndex]; }
                            else {
                              img.style.display = 'none';
                              const container = img.parentElement;
                              if (container) container.innerHTML = `<div style="text-align:center;padding:20px;"><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><p style="color:#999;font-size:12px;margin-top:10px;">Image not available</p></div>`;
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <ImageIcon sx={{ fontSize: 48, color: '#ccc' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>No image available</Typography>
                        </Box>
                      )}
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => window.open(getDeliveryProofImageUrl(proof.image), '_blank')} fullWidth>View Full Image</Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2196f3' }}>Open Box Delivery Proof</Typography>
            <Grid container spacing={2}>
              {deliveryProof.openbox_delivery.map((proof: any, index: number) => (
                <React.Fragment key={index}>
                  {proof.photo && (
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Photo</Typography>
                        <Box sx={{ width: '100%', height: 200, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', mb: 1, border: '1px solid #e0e0e0' }}>
                          <img src={getDeliveryProofImageUrl(proof.photo)} alt="Open box delivery photo" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { const img = e.target as HTMLImageElement; img.style.display = 'none'; }} />
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => window.open(getDeliveryProofImageUrl(proof.photo), '_blank')} fullWidth>View Photo</Button>
                      </Card>
                    </Grid>
                  )}
                  {proof.signature && (
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Signature</Typography>
                        <Box sx={{ width: '100%', height: 200, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', mb: 1, border: '1px solid #e0e0e0' }}>
                          <img src={getDeliveryProofImageUrl(proof.signature)} alt="Customer signature" style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white' }}
                            onError={(e) => { const img = e.target as HTMLImageElement; img.style.display = 'none'; }} />
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => window.open(getDeliveryProofImageUrl(proof.signature), '_blank')} fullWidth>View Signature</Button>
                      </Card>
                    </Grid>
                  )}
                  {proof.condition_status && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Condition Status</Typography>
                        <Chip label={proof.condition_status} size="small" color={proof.condition_status === 'OK' ? 'success' : 'warning'} />
                      </Box>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Box>
        )}

        {deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ff9800' }}>Partial Delivery Proof</Typography>
            <Grid container spacing={2}>
              {deliveryProof.partial_delivery.map((proof: any, index: number) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 2, border: '1px solid #ff9800' }}>
                    {proof.image && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Proof Image</Typography>
                        <Box sx={{ width: '100%', maxWidth: 300, height: 200, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', mb: 1, border: '1px solid #e0e0e0' }}>
                          <img src={getDeliveryProofImageUrl(proof.image)} alt="Partial delivery proof" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { const img = e.target as HTMLImageElement; img.style.display = 'none'; }} />
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => window.open(getDeliveryProofImageUrl(proof.image), '_blank')}>View Image</Button>
                      </Box>
                    )}
                    {proof.delivered_items && proof.delivered_items.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivered Items</Typography>
                        <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                          <Grid container spacing={1}>
                            {proof.delivered_items.map((item: any, idx: number) => (
                              <Grid item xs={12} key={idx}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: idx < proof.delivered_items.length - 1 ? '1px solid #ffe0b2' : 'none' }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name || `Item ${item.id}`}</Typography>
                                    <Typography variant="caption" color="text.secondary">ID: {item.id}</Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2">{item.deliveredQty || item.deliveredqty || 0} / {item.totalQty || item.totalqty || 0}</Typography>
                                    <Typography variant="caption" color="text.secondary">Delivered / Total</Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Box>
                    )}
                    {proof.reason && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Reason</Typography>
                        <Chip label={proof.reason} size="small" color="warning" sx={{ mb: 1 }} />
                      </Box>
                    )}
                    {proof.notes && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Notes</Typography>
                        <Paper sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}><Typography variant="body2">{proof.notes}</Typography></Paper>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  const handleCommentDialogClose = () => { setCommentDialogOpen(false); setCommentText(''); };

  const handleCombinedDialogClose = () => {
    setCombinedDialogOpen(false);
    setIsGeneratingCombined(false);
    setGenerationStep(0);
    setCombinedResult(null);
    if (combinedQrImage) { URL.revokeObjectURL(combinedQrImage); setCombinedQrImage(null); }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedCancelReason('customer_request');
    setCustomCancelReason('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            {!showDetails ? (
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223', fontSize: { xs: '20px', md: '24px' } }}>Orders</Typography>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs value={mainTabValue} onChange={(e, v) => { setMainTabValue(v); localStorage.setItem('order_management_state', JSON.stringify({ ...orderPersist, mainTabValue:v, searchStr })); }} sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                      <Tab label={`All (${totalRecords})`} />
                      <Tab label={`Cancelled Orders (${cancelledLineItems.length})`} />
                      <Tab label={`Refunds (${refundableItems.length})`} />
                    </Tabs>
                  </Box>

                  {mainTabValue === 0 && (<>
                  {/* Filters */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <TextField size="small" placeholder="Search orders" value={searchStr} onChange={handleSearch} fullWidth
                      sx={{ maxWidth: { xs: '100%', sm: 400 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment>) }}
                    />

                    <FormControl size="small" sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                      <InputLabel>Status</InputLabel>
                      <Select multiple value={statusFilter} onChange={handleStatusFilterChange} input={<OutlinedInput label="Status" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={getStatusText(value)} size="small" sx={{ backgroundColor: getStatusColor(value), color: 'white', fontSize: '11px', height: 20 }} />
                            ))}
                          </Box>
                        )}
                      >
                        {ORDER_STATUS_OPTIONS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={statusFilter.indexOf(status.value) > -1} />
                            <ListItemText primary={status.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                      <InputLabel>Warehouse</InputLabel>
                      <Select value={warehouseFilter} onChange={handleWarehouseFilterChange} label="Warehouse" sx={{ backgroundColor: 'white' }}>
                        <MenuItem value=""><em>All Warehouses</em></MenuItem>
                        {warehouses.map((w: any) => (
                          <MenuItem key={w.id} value={w.id}>{w.name || `Warehouse ${w.id}`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                      <InputLabel>Payment Method</InputLabel>
                      <Select multiple value={paymentFilter} onChange={handlePaymentFilterChange} input={<OutlinedInput label="Payment Method" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === value)?.label || value} size="small" sx={{ backgroundColor: '#5c6ac4', color: 'white', fontSize: '11px', height: 20 }} />
                            ))}
                          </Box>
                        )}
                      >
                        {PAYMENT_METHOD_OPTIONS.map((method) => (
                          <MenuItem key={method.value} value={method.value}>
                            <Checkbox checked={paymentFilter.indexOf(method.value) > -1} />
                            <ListItemText primary={method.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {(searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0) && (
                      <Button variant="outlined" onClick={handleClearFilters} sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, minWidth: { xs: '100%', sm: 'auto' }, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                        Clear Filters
                      </Button>
                    )}
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load orders. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && filteredOrders.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No orders found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0 ? 'No results matching your filters' : 'Orders will appear here when customers place them'}
                      </Typography>
                      {(searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0) && (
                        <Button variant="outlined" onClick={handleClearFilters} sx={{ textTransform: 'none', borderRadius: '8px' }}>Clear Filters</Button>
                      )}
                    </Box>
                  )}

                  {!isLoading && paginatedOrders.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {/* Scroll Navigation Bar */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                      }}>
                        <IconButton
                          size="small"
                          onClick={handleScrollLeft}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronLeft fontSize="small" />
                        </IconButton>

                        <Box sx={{
                          flex: 1,
                          height: '4px',
                          mx: 1,
                          bgcolor: '#e1e3e5',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: '30%',
                            bgcolor: '#6d7175',
                            borderRadius: '2px',
                            opacity: 0.4,
                          }} />
                        </Box>

                        <IconButton
                          size="small"
                          onClick={handleScrollRight}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Scrollable Table */}
                      <Box ref={tableScrollRef} sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 1280 }}>
                          {/* Header */}
                          <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '70px 170px 160px 110px 140px 70px 110px 200px', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
                            {['Order #', 'Customer', 'Actions', 'Date', 'Warehouse', 'Items', 'Total', 'Status'].map((header, i) => (
                              <Typography key={header} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: i === 2 ? 'center' : 'left' }}>
                                {header}
                              </Typography>
                            ))}
                          </Box>

                          {paginatedOrders.map((order: Order, index: number) => (
                            <React.Fragment key={order.id}>
                              {/* Desktop row */}
                              <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '70px 170px 160px 110px 140px 70px 110px 200px', gap: 2, px: 2, py: 2, alignItems: 'center', borderBottom: index < paginatedOrders.length - 1 ? '1px solid #e1e3e5' : 'none', '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>#{order.id}</Typography>

                                <Box title={String(order?.order_id || order?.id || '')} sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }} noWrap>{order.user_name}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>ID: {order.user_id}</Typography>
                                </Box>
<Box
                                  sx={{
                                    width: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 30px)',
                                    gridAutoRows: '30px',
                                    gap: 0.75,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    justifyItems: 'center',
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  <Tooltip title="View Details" arrow placement="top">
                                    <IconButton size="small" onClick={() => handleViewOrder(order)} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#6d7175', border: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                      <Visibility sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Update Status" arrow placement="top">
                                    <IconButton size="small" onClick={() => handleStatusUpdateClick(order)} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#6d7175', border: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                      <Edit sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>

                                  {order.status <= CANCELLABLE_MAX_STATUS && (
                                    <Tooltip title="Cancel Order" arrow placement="top">
                                      <IconButton size="small" onClick={() => handleCancelOrderClick(order)} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#f44336', border: '1px solid #ffcdd2', '&:hover': { bgcolor: '#ffebee' } }}>
                                        <Cancel sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  <Tooltip title="Generate Invoice & QR" arrow placement="top">
                                    <span>
                                      <IconButton size="small" onClick={() => handleGenerateCombined(order)} disabled={isGeneratingCombined} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#9c27b0', border: '1px solid #e1bee7', backgroundColor: isGeneratingCombined ? '#f3e5f5' : '#fff', '&:hover': { bgcolor: '#f3e5f5', color: '#7b1fa2' } }}>
                                        {isGeneratingCombined ? <CircularProgress size={16} /> : <FileCopy sx={{ fontSize: 17 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title="Generate QR Code" arrow placement="top">
                                    <span>
                                      <IconButton size="small" onClick={() => handleGenerateQR(order)} disabled={isGeneratingQR} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#5c6ac4', border: '1px solid #c5cae9', backgroundColor: isGeneratingQR ? '#eeeffc' : '#fff', '&:hover': { bgcolor: '#eeeffc', color: '#4c5ab8' } }}>
                                        {isGeneratingQR ? <CircularProgress size={16} /> : <QrCode2 sx={{ fontSize: 17 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title="Open Invoice" arrow placement="top">
                                    <span>
                                      <IconButton size="small" onClick={() => handleOpenInvoice(order)} disabled={isGeneratingInvoice} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#008060', border: '1px solid #b7dfd2', backgroundColor: isGeneratingInvoice ? '#e3f5f0' : '#fff', '&:hover': { bgcolor: '#e3f5f0', color: '#006e52' } }}>
                                        {isGeneratingInvoice ? <CircularProgress size={16} /> : <Receipt sx={{ fontSize: 17 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  {hasAnyComments(order.id) && (
                                    <Tooltip title="View Comments" arrow placement="top">
                                      <IconButton size="small" onClick={() => handleViewComments(order)} sx={{ width: 30, height: 30, borderRadius: '6px', color: '#6d7175', border: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                        <Comment sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
<Typography variant="body2" color="text.secondary" noWrap>{formatDate(order.order_date)}</Typography>
<Box title={String(order?.shipping_address || order?.address || '')} sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <Typography variant="body2" color="text.secondary" noWrap>{getOrderWarehouseName(order)}</Typography>
                                </Box>
<Typography variant="body2" color="text.secondary" noWrap>{getVisibleItemsCount(order)}</Typography>
<Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }} noWrap>{formatCurrency(getOrderBalanceAmount(order))}</Typography>
<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                  {getStatusChip(order.status)}
                                  {hasAnyComments(order.id) && <Comment color="info" fontSize="small" />}
                                  {!HIDE_AGENT_STATUSES.includes(order.status) ? (() => {
                                    const agent = (order as any).delivery_agent || null;
                                    if (!agent && !order.delivery_agent_name) {
                                      return <Typography variant="caption" color="text.secondary" noWrap>Agent: Not assigned</Typography>;
                                    }
                                    const name = order.delivery_agent_name || agent?.first_name || agent?.firstName || agent?.user_name || `${agent?.first_name || ''} ${agent?.last_name || ''}`.trim() || `Agent ${agent?.id || ''}`;
                                    const contact = agent?.contact || agent?.mobile || agent?.phone || null;
                                    return (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6a00', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</Typography>
                                        {contact && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={contact}>Agent: {contact}</Typography>}
                                      </Box>
                                    );
                                  })() : null}
                                </Box>                              </Box>

                              {/* Mobile card */}
                              <Box sx={{ display: { xs: 'block', md: 'none' }, m: 2 }}>
                                <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                                  <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223', fontSize: '16px' }}>Order #{order.id}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatDate(order.order_date)}</Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getStatusChip(order.status)}
                                        {hasAnyComments(order.id) && <Comment color="info" fontSize="small" sx={{ ml: 0.5 }} />}
                                        <IconButton size="small" onClick={() => handleToggleRow(order.id)} sx={{ ml: 1 }}>
                                          {expandedRows.includes(order.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                        </IconButton>
                                      </Box>
                                    </Box>

                                    <Grid container spacing={1}>
                                      <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Customer</Typography><Typography variant="body2" sx={{ fontWeight: 500 }}>{order.user_name}</Typography></Grid>
                                      <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>ID</Typography><Typography variant="body2">{order.user_id}</Typography></Grid>
                                      <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Warehouse</Typography><Typography variant="body2">{getOrderWarehouseName(order)}</Typography></Grid>
                                      {!HIDE_AGENT_STATUSES.includes(order.status) && (
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Agent</Typography><Typography variant="body2">{order.delivery_agent_name || 'Not assigned'}</Typography></Grid>
                                      )}
                                      <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Items</Typography><Typography variant="body2" sx={{ fontWeight: 500 }}>{getVisibleItemsCount(order)} item{getVisibleItemsCount(order) !== 1 ? 's' : ''}</Typography></Grid>
                                      <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Total</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{formatCurrency(getOrderBalanceAmount(order))}</Typography></Grid>
                                    </Grid>

                                    <Collapse in={expandedRows.includes(order.id)}>
                                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e1e3e5' }}>
                                        {!HIDE_AGENT_STATUSES.includes(order.status) && order.delivery_agent_name && (
                                          <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Delivery Agent Details</Typography>
                                            {renderAgentInfo(false, { name: order.delivery_agent_name, contact: (order as any).delivery_agent?.contact || null, id: (order as any).delivery_agent?.id || null })}
                                          </Box>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                          <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handleViewOrder(order)} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>View</Button>
                                          <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleStatusUpdateClick(order)} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>Update</Button>
                                          {order.status <= CANCELLABLE_MAX_STATUS && (
                                            <Button size="small" variant="outlined" color="error" startIcon={<Cancel />} onClick={() => handleCancelOrderClick(order)} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none' }}>Cancel</Button>
                                          )}
                                          <Button size="small" variant="contained" startIcon={isGeneratingCombined ? <CircularProgress size={16} /> : <FileCopy />} onClick={() => handleGenerateCombined(order)} disabled={isGeneratingCombined} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none', bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}>Generate</Button>
                                          <Button size="small" variant="contained" startIcon={isGeneratingQR ? <CircularProgress size={16} /> : <QrCode2 />} onClick={() => handleGenerateQR(order)} disabled={isGeneratingQR} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none', bgcolor: '#5c6ac4', '&:hover': { bgcolor: '#4c5ab8' } }}>QR Code</Button>
                                          <Button size="small" variant="contained" startIcon={isGeneratingInvoice ? <CircularProgress size={16} /> : <Receipt />} onClick={() => handleOpenInvoice(order)} disabled={isGeneratingInvoice} sx={{ flex: '1 1 auto', minWidth: '100px', textTransform: 'none', bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}>Invoice</Button>
                                          {hasAnyComments(order.id) && (
                                            <Button size="small" variant="outlined" startIcon={<Comment />} onClick={() => handleViewComments(order)} sx={{ flex: '1 1 100%', textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>Comments</Button>
                                          )}
                                        </Box>
                                      </Box>
                                    </Collapse>

                                    {!expandedRows.includes(order.id) && (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                        <Button size="small" variant="outlined" onClick={() => handleViewOrder(order)} sx={{ flex: 1, textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>View</Button>
                                        <Button size="small" variant="outlined" onClick={() => handleStatusUpdateClick(order)} sx={{ flex: 1, textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>Update</Button>
                                        {order.status <= CANCELLABLE_MAX_STATUS && (
                                          <Button size="small" variant="outlined" color="error" onClick={() => handleCancelOrderClick(order)} sx={{ flex: 1, textTransform: 'none' }}>Cancel</Button>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                </Card>
                              </Box>
                            </React.Fragment>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  </>)}

                  {mainTabValue === 1 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                        <TextField size="small" placeholder="Search by order #, customer, or product" value={searchStr} onChange={handleSearch} fullWidth
                          sx={{ maxWidth: { xs: '100%', sm: 400 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment>) }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Every cancelled item, across every order, with how it was refunded — the reference list for processing/reconciling refunds.
                        </Typography>
                      </Box>

                      {filteredCancelledItems.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No cancelled items</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchStr ? 'No results matching your search' : 'Cancelled order items will appear here as soon as an order or item is cancelled.'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                          <Box sx={{ minWidth: 1100 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '80px 160px 1fr 90px 100px 170px 130px 90px', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
                              {['Order #', 'Customer', 'Product', 'Qty', 'Value', 'Refund Mode', 'Cancelled On', 'Actions'].map((header) => (
                                <Typography key={header} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>{header}</Typography>
                              ))}
                            </Box>

                            {paginatedCancelledItems.map((row, index) => {
                              const refundLabel = row.refundMode;
                              const isCod = refundLabel.toLowerCase().includes('cod');
                              const isPending = refundLabel === 'Pending';
                              return (
                                <Box key={row.key} sx={{ display: 'grid', gridTemplateColumns: '80px 160px 1fr 90px 100px 170px 130px 90px', gap: 2, px: 2, py: 2, alignItems: 'center', borderBottom: index < paginatedCancelledItems.length - 1 ? '1px solid #e1e3e5' : 'none', '&:hover': { bgcolor: '#f9fafb' } }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>#{row.orderId}</Typography>

                                  <Box title={String(row?.userName || '')} sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }} noWrap>{row.userName}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>ID: {row.userId}</Typography>
                                  </Box>

                                  <Box sx={{ overflow: 'hidden' }}>
                                    <Typography variant="body2" noWrap>{row.productName}</Typography>
                                    {row.reason && row.reason !== '-' && (
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>Reason: {row.reason}</Typography>
                                    )}
                                  </Box>

                                  <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>{row.cancelledQty}/{row.totalQty}</Typography>

                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(row.value)}</Typography>

                                  <Box>
                                    <Chip
                                      label={refundLabel}
                                      size="small"
                                      sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        maxWidth: 160,
                                        bgcolor: isCod ? '#fff4e5' : isPending ? '#fdecea' : '#e3f7ec',
                                        color: isCod ? '#95600a' : isPending ? '#c62828' : '#0f7b45',
                                      }}
                                    />
                                    {row.transactionId && (
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.5 }}>
                                        Txn ID: {row.transactionId}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Typography variant="body2" color="text.secondary" noWrap>{formatDate(row.cancelledAt)}</Typography>

                                  <Button size="small" variant="outlined" onClick={() => handleViewOrder(row.order)} sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>View</Button>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {mainTabValue === 2 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                        <TextField size="small" placeholder="Search by order #, customer, or product" value={searchStr} onChange={handleSearch} fullWidth
                          sx={{ maxWidth: { xs: '100%', sm: 400 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment>) }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Cancelled products where money was actually collected from the customer — COD rows (nothing to refund) are excluded. This is the list to reconcile against wallet/pay-later credits.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                          <Box sx={{ px: 2, py: 1, borderRadius: '8px', bgcolor: '#e3f7ec', minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: '#0f7b45', fontWeight: 600 }}>Refunded</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f7b45' }}>{refundSummary.completedCount} items · {formatCurrency(refundSummary.completedValue)}</Typography>
                          </Box>
                          <Box sx={{ px: 2, py: 1, borderRadius: '8px', bgcolor: '#fdecea', minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>Pending / Needs Attention</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>{refundSummary.pendingCount} items · {formatCurrency(refundSummary.pendingValue)}</Typography>
                          </Box>
                        </Box>
                      </Box>

                      {filteredRefundableItems.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No refunds to show</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchStr ? 'No results matching your search' : 'A refund-due row appears here whenever a paid item is cancelled (COD cancellations are excluded, since nothing was collected).'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                          <Box sx={{ minWidth: 1180 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '80px 160px 1fr 90px 100px 250px 130px 90px', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
                              {['Order #', 'Customer', 'Product', 'Qty', 'Refund Amt', 'Refund Mode', 'Cancelled On', 'Actions'].map((header) => (
                                <Typography key={header} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>{header}</Typography>
                              ))}
                            </Box>

                            {paginatedRefundItems.map((row, index) => {
                              const refundLabel = row.refundMode;
                              const isCompleted = refundLabel.toLowerCase().includes('credited');
                              return (
                                <Box key={row.key} sx={{ display: 'grid', gridTemplateColumns: '80px 160px 1fr 90px 100px 250px 130px 90px', gap: 2, px: 2, py: 2, alignItems: 'flex-start', borderBottom: index < paginatedRefundItems.length - 1 ? '1px solid #e1e3e5' : 'none', '&:hover': { bgcolor: '#f9fafb' } }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>#{row.orderId}</Typography>

                                  <Box title={String(row?.userName || '')} sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }} noWrap>{row.userName}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>ID: {row.userId}</Typography>
                                  </Box>

                                  <Box sx={{ overflow: 'hidden' }}>
                                    <Typography variant="body2" noWrap>{row.productName}</Typography>
                                    {row.reason && row.reason !== '-' && (
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>Reason: {row.reason}</Typography>
                                    )}
                                  </Box>

                                  <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>{row.cancelledQty}/{row.totalQty}</Typography>

                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(row.value)}</Typography>

                                  <Box>
                                    <Chip
                                      label={isCompleted ? refundLabel : `Awaiting: ${refundLabel}`}
                                      size="small"
                                      sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        height: 'auto',
                                        bgcolor: isCompleted ? '#e3f7ec' : '#fdecea',
                                        color: isCompleted ? '#0f7b45' : '#c62828',
                                        // Let the full refund-mode text wrap onto multiple
                                        // lines instead of being clipped with "..." — the
                                        // full text (e.g. "Refunded to Original Payment
                                        // Method (Card)") matters for Finance reconciliation.
                                        '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.5, lineHeight: 1.3 },
                                      }}
                                    />
                                    {row.transactionId && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                                        Txn ID: {row.transactionId}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Typography variant="body2" color="text.secondary" noWrap>{formatDate(row.cancelledAt)}</Typography>

                                  <Button size="small" variant="outlined" onClick={() => handleViewOrder(row.order)} sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}>View</Button>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Card>

                {mainTabValue === 0 && totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={totalPages} page={page} onChange={(e, newPage) => setPage(newPage)}
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }}
                    />
                  </Box>
                )}

                {mainTabValue === 1 && cancelledTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={cancelledTotalPages} page={cancelledPage} onChange={(e, newPage) => setCancelledPage(newPage)}
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }}
                    />
                  </Box>
                )}

                {mainTabValue === 2 && refundTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={refundTotalPages} page={refundPage} onChange={(e, newPage) => setRefundPage(newPage)}
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // ORDER DETAILS VIEW
              <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box sx={{ mb: 3 }}>
                  <Button startIcon={<ArrowBack />} onClick={handleBackToList} sx={{ color: '#202223', textTransform: 'none', fontWeight: 500, mb: 2, '&:hover': { bgcolor: 'transparent' } }}>Orders</Button>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223', fontSize: { xs: '20px', md: '24px' } }}>Order #{selectedOrder?.id}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>{getStatusChip(selectedOrder?.status || 0)}</Box>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  {/* Customer */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223', display: 'flex', alignItems: 'center', gap: 1 }}><Person /> Customer</Typography>
                        {isLoadingDetails ? (
                          <Box><Skeleton variant="text" width="40%" height={24} /><Skeleton variant="text" width="60%" height={20} /></Box>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 1 }}><strong>Name:</strong> {customerData?.name || selectedOrder?.user_name}</Typography>
                            <Typography variant="body2"><strong>User ID:</strong> {customerData?.userId || selectedOrder?.user_id}</Typography>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Delivery Agent */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Delivery Agent</Typography>
                        {renderAgentInfo(isLoadingDetails, deliveryAgentData)}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Warehouse Information */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Warehouse Information</Typography>
                        {isLoadingDetails ? (
                          <Box><Skeleton variant="text" width="70%" height={24} /><Skeleton variant="text" width="50%" height={20} /></Box>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 1 }}><strong>Warehouse:</strong> {warehouseData?.name || getOrderWarehouseName(selectedOrder || {} as Order)}</Typography>
                            <Typography variant="body2"><strong>Order Date:</strong> {formatDate(warehouseData?.orderDate || selectedOrder?.order_date || '')}</Typography>
                          </>
                        )}
                      </Box>
                      {Number(selectedOrder?.gift_delivery) === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, px: 3 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: '#d32f2f' }}>
                            GIFT ORDER <CardGiftcardIcon fontSize="medium" color="error" />
                          </Typography>
                          <Typography variant="body2"><strong>Gift Message:</strong> {selectedOrder?.gift_message || ''}</Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  {/* Delivery Information */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Delivery Information</Typography>
                        {isLoadingDetails ? (
                          <Box>
                            <Skeleton variant="text" width="50%" height={24} />
                            <Skeleton variant="text" width="80%" height={20} />
                            <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2, borderRadius: 1 }} />
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>Delivery Type</Typography>
                              <Chip label={deliveryInfoData?.deliveryType || getDeliveryType(selectedOrder)} size="small" sx={{ backgroundColor: getDeliveryTypeColor(deliveryInfoData?.deliveryType || getDeliveryType(selectedOrder)), color: 'white', fontWeight: 600 }} />
                            </Box>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>Assignment ID</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 1, borderRadius: 1, wordBreak: 'break-all' }}>{deliveryInfoData?.assignmentId || getAssignmentId(selectedOrder)}</Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#666' }}>Delivery Proof</Typography>
                              {renderDeliveryProofImages(selectedOrder)}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Payment Summary */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 4 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223', display: 'flex', alignItems: 'center', gap: 1 }}><AttachMoney /> Payment Summary</Typography>

                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>Payment Method</Typography>
                          {selectedOrder?.cash_on_delivery === 1 ? (
                            <Chip label="Cash on Delivery" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 600 }} />
                          ) : selectedOrder?.pay_later === 1 ? (
                            <Chip label="Pay Later" size="small" sx={{ backgroundColor: '#2196f3', color: 'white', fontWeight: 600, mb: 1 }} />
                          ) : (
                            <Chip label="Online Payment" size="small" sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 600 }} />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                          <Typography variant="body2">{formatCurrency(selectedOrder?.total_amount || '0')}</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Shipping</Typography>
                          <Typography variant="body2">{formatCurrency(selectedOrder?.total_shipping_charge || '0')}</Typography>
                        </Box>

                        {(() => {
                          const totalOpenBox = (selectedOrder?.items || []).reduce(
                            (sum: number, item: any) => sum + Number(item.open_box_charge || 0),
                            0
                          );
                          return totalOpenBox > 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Open Box Charge</Typography>
                              <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 500 }}>
                                {formatCurrency(totalOpenBox.toFixed(2))}
                              </Typography>
                            </Box>
                          ) : null;
                        })()}

                        {Number(selectedOrder?.reward_discount) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Reward Discount</Typography>
                            <Typography variant="body2" sx={{ color: '#e53935', fontWeight: 500 }}>
                              -{formatCurrency(selectedOrder?.reward_discount || '0')}
                            </Typography>
                          </Box>
                        )}

                        {Number(selectedOrder?.coupon_discount) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Coupon Discount</Typography>
                            <Typography variant="body2" sx={{ color: '#e53935', fontWeight: 500 }}>
                              -{formatCurrency(selectedOrder?.coupon_discount || '0')}
                            </Typography>
                          </Box>
                        )}

                        {selectedOrder?.pay_later === 1 && Number((selectedOrder as any)?.pay_later_discount) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Pay Later Discount</Typography>
                            <Typography variant="body2" sx={{ color: '#e53935', fontWeight: 500 }}>
                              -{formatCurrency((selectedOrder as any)?.pay_later_discount || '0')}
                            </Typography>
                          </Box>
                        )}

                        {Number(selectedOrder?.total_warranty_amount) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Warranty</Typography>
                            <Typography variant="body2" sx={{ color: '#e0212a', fontWeight: 500 }}>
                              {formatCurrency(selectedOrder?.total_warranty_amount || '0')}
                            </Typography>
                          </Box>
                        )}

                        {(() => {
                          const cancelledValue = getCancelledItemsValue(selectedOrder?.items || []);
                          if (cancelledValue <= 0) return null;
                          return (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Cancelled Item(s)</Typography>
                              <Typography variant="body2" sx={{ color: '#e53935', fontWeight: 500 }}>
                                -{formatCurrency(cancelledValue.toFixed(2))}
                              </Typography>
                            </Box>
                          );
                        })()}

                        {(() => {
                          const cancelledValue = getCancelledItemsValue(selectedOrder?.items || []);
                          const balance = Math.max(Number(selectedOrder?.total_order_amount || 0) - cancelledValue, 0);
                          return (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '2px solid #e1e3e5' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{cancelledValue > 0 ? 'Balance Amount' : 'Total'}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(balance.toFixed(2))}</Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Actions</Typography>
                        <Stack spacing={1}>
                          <Button variant="contained" fullWidth onClick={() => selectedOrder && handleStatusUpdateClick(selectedOrder)} startIcon={<Edit />} sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#006e52' } }}>Update Status</Button>
                          {selectedOrder && selectedOrder.status <= CANCELLABLE_MAX_STATUS && (
                            <Button variant="contained" fullWidth onClick={() => selectedOrder && handleCancelOrderClick(selectedOrder)} startIcon={<Cancel />} sx={{ bgcolor: '#f44336', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#d32f2f' } }}>Cancel Order</Button>
                          )}
                          <Button variant="contained" fullWidth onClick={() => selectedOrder && handleGenerateCombined(selectedOrder)} startIcon={<FileCopy />} disabled={isGeneratingCombined} sx={{ bgcolor: '#9c27b0', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#7b1fa2' } }}>
                            {isGeneratingCombined ? 'Generating...' : 'Generate Invoice & QR'}
                          </Button>
                          <Button variant="contained" fullWidth onClick={() => selectedOrder && handleGenerateQR(selectedOrder)} startIcon={<QrCode2 />} disabled={isGeneratingQR} sx={{ bgcolor: '#5c6ac4', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#4c5ab8' } }}>
                            {isGeneratingQR ? 'Generating...' : 'Generate QR Code'}
                          </Button>
                          {hasAnyComments(selectedOrder?.id || 0) && (
                            <Button variant="outlined" fullWidth onClick={() => selectedOrder && handleViewComments(selectedOrder)} startIcon={<Comment />} sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>View All Comments</Button>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Products */}
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Products ({selectedOrder?.order_items_count ?? (selectedOrder?.items ? selectedOrder.items.length : 0)})
                        </Typography>
                        {renderOrderItems(selectedOrder)}
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f44336', color: 'white' }}>
          <Typography variant="h6">Cancel Order #{selectedOrder?.id}</Typography>
          <IconButton onClick={handleCancelDialogClose} size="small" sx={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>You are about to cancel this order. This action cannot be undone.</Typography>
            {selectedOrder && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Order Summary:</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="body2"><strong>Customer:</strong> {selectedOrder.user_name}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="body2"><strong>Amount:</strong> {formatCurrency(getOrderBalanceAmount(selectedOrder))}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="body2"><strong>Payment:</strong> {selectedOrder.cash_on_delivery === 1 ? 'Cash on Delivery' : selectedOrder.pay_later === 1 ? 'Pay Later' : (selectedOrder as any)?.wallet === 1 ? 'Wallet' : 'Online Payment'}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="body2"><strong>Items:</strong> {getVisibleItemsCount(selectedOrder)}</Typography></Grid>
                </Grid>
              </Box>
            )}
            {selectedOrder && selectedOrder.cash_on_delivery === 1 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                <Typography variant="body2">This order was Cash on Delivery — no payment was collected, so no refund is due.</Typography>
              </Box>
            )}
            {selectedOrder && selectedOrder.pay_later === 1 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalanceWallet sx={{ mr: 1, color: '#2e7d32' }} />
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>Automatic Refund</Typography>
                </Box>
                <Typography variant="body2">
                  {formatCurrency(getOrderBalanceAmount(selectedOrder))} will be refunded to customer's Pay Later balance.
                </Typography>
              </Box>
            )}
            {selectedOrder && selectedOrder.cash_on_delivery !== 1 && selectedOrder.pay_later !== 1 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalanceWallet sx={{ mr: 1, color: '#2e7d32' }} />
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                    Refund {formatCurrency(getOrderBalanceAmount(selectedOrder))} to:
                  </Typography>
                </Box>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={refundDestination}
                    onChange={(e) => setRefundDestination(e.target.value as 'wallet' | 'original')}
                  >
                    <FormControlLabel
                      value="original"
                      control={<Radio size="small" />}
                      label={<Box><Typography variant="body2">{`Original Payment Method${(selectedOrder as any)?.payment_method ? ` (${(selectedOrder as any).payment_method})` : ''}`}</Typography><Typography variant="caption" color="text.secondary">{(selectedOrder as any)?.wallet === 1 ? 'This order was paid using Wallet, so the refund goes back to the Wallet — instant, same as choosing Wallet above' : `Refund is initiated back to ${(selectedOrder as any)?.payment_method || 'the card/online payment'} used to pay — may take a few days via the gateway`}</Typography></Box>}
                    />
                    <FormControlLabel
                      value="wallet"
                      control={<Radio size="small" />}
                      label={<Box><Typography variant="body2">Wallet (Voto Credits)</Typography><Typography variant="caption" color="text.secondary">Instant — customer can use it on their next order right away</Typography></Box>}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Cancellation Type</Typography>
              <FormControl component="fieldset">
                <RadioGroup value={cancelType} onChange={(e) => setCancelType(e.target.value as 'admin' | 'user')}>
                  <FormControlLabel value="admin" control={<Radio />} label={<Box><Typography variant="body2">Admin Cancellation</Typography><Typography variant="caption" color="text.secondary">Order status will be: "Order cancelled by admin"</Typography></Box>} />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Cancellation Reason</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Reason</InputLabel>
                <Select value={selectedCancelReason} onChange={(e) => setSelectedCancelReason(e.target.value)} label="Select Reason">
                  {CANCEL_REASON_OPTIONS.map((reason) => (<MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>))}
                </Select>
              </FormControl>
              {selectedCancelReason === 'other' && (
                <TextField fullWidth label="Custom Reason" multiline rows={3} value={customCancelReason} onChange={(e) => setCustomCancelReason(e.target.value)} placeholder="Please specify the cancellation reason..." sx={{ mb: 2 }} />
              )}
              <Typography variant="caption" color="text.secondary">This reason will be recorded in the order history and may be shared with the customer.</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCancelDialogClose} disabled={isCancelling} sx={{ bgcolor: '#f44336', color: '#eeeeee' }}>Close</Button>
          <Button onClick={handleCancelDialogClose} disabled={isCancelling} sx={{ bgcolor: '#F78E12', color: '#eeeeee' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleCancelOrder} disabled={isCancelling || (selectedCancelReason === 'other' && !customCancelReason.trim())} startIcon={isCancelling ? <CircularProgress size={20} /> : <Cancel />}>
            {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Combined Generation Dialog */}
      <Dialog open={combinedDialogOpen} onClose={handleCombinedDialogClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: '600px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#9c27b0', color: 'white' }}>
          <Typography variant="h6">Generate Invoice & QR Code</Typography>
          <IconButton onClick={handleCombinedDialogClose} size="small" sx={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isGeneratingCombined ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <Stepper activeStep={generationStep} orientation="vertical" sx={{ width: '100%' }}>
                {generationSteps.map((step, index) => (
                  <Step key={step.label} completed={generationStep > index}>
                    <StepLabel StepIconProps={{ icon: step.icon }}>
                      <Typography variant="subtitle1" fontWeight="bold">{step.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                    </StepLabel>
                    <StepContent>
                      {index === 0 && generationStep === 1 && <Box sx={{ pl: 4, py: 2 }}><CircularProgress size={20} sx={{ mr: 2 }} /><Typography variant="body2">Generating invoice PDF...</Typography></Box>}
                      {index === 1 && generationStep === 2 && <Box sx={{ pl: 4, py: 2 }}><CircularProgress size={20} sx={{ mr: 2 }} /><Typography variant="body2">Creating QR code...</Typography></Box>}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              {generationStep < 3 && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ color: '#9c27b0' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Please wait while we generate everything...</Typography>
                </Box>
              )}
            </Box>
          ) : combinedResult ? (
            <Box ref={combinedPrintRef}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mb: 3, border: '2px solid #e0e0e0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ color: '#9c27b0', fontWeight: 'bold' }}><Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />INVOICE</Typography>
                      <Chip label={combinedResult.invoice ? "Generated" : "Failed"} color={combinedResult.invoice ? "success" : "error"} icon={combinedResult.invoice ? <CheckCircle /> : <ErrorIcon />} />
                    </Box>
                    {combinedResult.invoice ? (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}><Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}><Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography><Typography variant="h6" sx={{ fontWeight: 'bold' }}>{combinedResult.invoice.invoice_no}</Typography></Box></Grid>
                          <Grid item xs={12} md={6}><Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}><Typography variant="subtitle2" color="text.secondary">Order Details</Typography><Typography variant="body2">Order #{selectedOrder?.id} | {selectedOrder?.user_name}</Typography></Box></Grid>
                        </Grid>
                        <Box sx={{ mt: 3, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}><strong>Invoice PDF is ready for download.</strong></Typography>
                          <Typography variant="body2" color="text.secondary">The PDF contains complete invoice details including items, taxes, and totals.</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}><ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} /><Typography variant="h6" color="error" gutterBottom>Invoice Generation Failed</Typography><Typography variant="body2" color="text.secondary">{combinedResult.message || 'Unable to generate invoice'}</Typography></Box>
                    )}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      {combinedResult.invoice && (
                        <>
                          <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadCombinedInvoice} sx={{ textTransform: 'none' }}>Download Invoice</Button>
                          <Button variant="contained" startIcon={<Print />} onClick={async () => { await handleAutoUpdateToProcessing(); window.open(combinedResult.invoice!.download_url, '_blank'); setSnackbar({ open: true, message: 'Invoice opened in new tab. Use Ctrl+P to print.', severity: 'info' }); }} sx={{ textTransform: 'none', bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}>Open Invoice</Button>
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 3, border: '2px solid #e0e0e0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ color: '#008060', fontWeight: 'bold' }}><QrCode2 sx={{ mr: 1, verticalAlign: 'middle' }} />QR CODE FOR PICKUP</Typography>
                      <Chip label={combinedResult.qr ? "Generated" : "Failed"} color={combinedResult.qr ? "success" : "error"} icon={combinedResult.qr ? <CheckCircle /> : <ErrorIcon />} />
                    </Box>
                    {combinedResult.qr ? (
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ width: '100%', maxWidth: 300, mx: 'auto', p: 3, border: '2px solid #e0e0e0', borderRadius: 2, bgcolor: 'white', mb: 3 }}>
                              {combinedQrImage ? <img src={combinedQrImage} alt="QR Code" style={{ width: '100%', height: 'auto' }} /> : <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading QR Code...</Typography></Box>}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>PICKUP INFORMATION</Typography>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" color="text.secondary" display="block">PICKUP CODE</Typography>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', fontFamily: 'monospace', letterSpacing: 3, color: '#333', bgcolor: 'white', p: 2, borderRadius: 1, mt: 1 }}>{combinedResult.qr.pickup_code}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" gutterBottom><strong>Instructions:</strong></Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>1. Scan QR code or enter pickup code</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>2. Verify order details</Typography>
                              <Typography variant="body2" color="text.secondary">3. Hand over items to customer</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}><ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} /><Typography variant="h6" color="error" gutterBottom>QR Code Generation Failed</Typography><Typography variant="body2" color="text.secondary">{combinedResult.message || 'Unable to generate QR code'}</Typography></Box>
                    )}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      {combinedResult.qr && (
                        <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadCombinedQR} sx={{ textTransform: 'none' }}>Download QR</Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 3, mt: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>GENERATION SUMMARY</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary" display="block">Order ID</Typography><Typography variant="h6">#{selectedOrder?.id}</Typography></Box></Grid>
                  <Grid item xs={6} md={3}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary" display="block">Customer</Typography><Typography variant="body1">{selectedOrder?.user_name}</Typography></Box></Grid>
                  <Grid item xs={6} md={3}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary" display="block">Invoice</Typography><Chip label={combinedResult.invoice ? "✓ Ready" : "✗ Failed"} color={combinedResult.invoice ? "success" : "error"} size="small" /></Box></Grid>
                  <Grid item xs={6} md={3}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary" display="block">QR Code</Typography><Chip label={combinedResult.qr ? "✓ Ready" : "✗ Failed"} color={combinedResult.qr ? "success" : "error"} size="small" /></Box></Grid>
                </Grid>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">Ready to generate invoice and QR code for Order #{selectedOrder?.id}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Click the Generate button to start</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button onClick={handleCombinedDialogClose} sx={{ textTransform: 'none' }}>Close</Button>
          {combinedResult && (
            <Button variant="contained" startIcon={<Print />} onClick={handlePrintCombined} disabled={!combinedResult.invoice || !combinedResult.qr} sx={{ textTransform: 'none', bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}>Print Both Pages</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => { setQrDialogOpen(false); if (qrImageUrl) { try { URL.revokeObjectURL(qrImageUrl); } catch { } setQrImageUrl(null); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Order QR Code</Typography>
          <IconButton onClick={() => { setQrDialogOpen(false); if (qrImageUrl) { try { URL.revokeObjectURL(qrImageUrl); } catch { } setQrImageUrl(null); } }} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <div ref={qrPrintRef}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>Order #{selectedOrder?.id}</Typography>
                <Typography variant="body2" color="text.secondary">Customer: {selectedOrder?.user_name}</Typography>
                <Typography variant="body2" color="text.secondary">Date: {selectedOrder?.order_date ? formatDate(selectedOrder.order_date) : '-'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>Total: {selectedOrder ? formatCurrency(getOrderBalanceAmount(selectedOrder)) : '-'}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              {generatedQR && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    {isLoadingQrImage ? <CircularProgress /> : (
                      <img src={qrImageUrl || `data:image/png;base64,${generatedQR.qrCode}`} alt="Order QR Code"
                        style={{ maxWidth: '300px', width: '100%', border: '2px solid #e1e3e5', borderRadius: '8px', backgroundColor: 'white' }}
                        onError={(e) => { const img = e.target as HTMLImageElement; if (!img.src.startsWith('data:') && generatedQR?.qrCode) img.src = `data:image/png;base64,${generatedQR.qrCode}`; }}
                      />
                    )}
                  </Box>
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 3, fontFamily: 'monospace' }}>{generatedQR.pickupCode}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => { setQrDialogOpen(false); if (qrImageUrl) { try { URL.revokeObjectURL(qrImageUrl); } catch { } setQrImageUrl(null); } }} sx={{ textTransform: 'none' }}>Close</Button>
          <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadQR} disabled={!generatedQR} sx={{ textTransform: 'none' }}>Download</Button>
          <Button variant="contained" startIcon={<Print />} onClick={handlePrintQR} disabled={!generatedQR} sx={{ textTransform: 'none', bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}>Print</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select value={selectedStatus} onChange={(e) => setSelectedStatus(Number(e.target.value))} label="Order Status">
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: option.color }} />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {requiresComment(selectedStatus) ? '⚠️ Comment is required for this status update' : '💡 Comment is optional for this status update'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={isUpdating} sx={{ bgcolor: '#f44336', color: '#eeeeee' }}>Close</Button>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={isUpdating} sx={{ bgcolor: '#F78E12', color: '#eeeeee' }}>Cancel</Button>
          <Button variant="contained" onClick={() => { if (requiresComment(selectedStatus)) { setStatusDialogOpen(false); setCommentDialogOpen(true); } else { handleStatusUpdate(); setStatusDialogOpen(false); } }} disabled={isUpdating} startIcon={isUpdating ? <CircularProgress size={20} /> : null}>
            {requiresComment(selectedStatus) ? 'Add Comment' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={handleCommentDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Update Order Status {requiresComment(selectedStatus) ? '(Comment Required)' : '(Comment Optional)'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select value={selectedStatus} onChange={(e) => { const newStatus = Number(e.target.value); setSelectedStatus(newStatus); if (requiresComment(newStatus) && !commentText.trim()) { const opt = ORDER_STATUS_OPTIONS.find(o => o.value === newStatus); setCommentText(`Status updated to: ${opt?.label || getStatusText(newStatus)}`); } }} label="Order Status">
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: option.color }} />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={4} label={requiresComment(selectedStatus) ? 'Add a comment (required)' : 'Add a comment (optional)'} value={commentText} onChange={(e) => setCommentText(e.target.value)}
            placeholder={`Status updated to: ${ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus)?.label || getStatusText(selectedStatus)}`}
            required={requiresComment(selectedStatus)} error={requiresComment(selectedStatus) && !commentText.trim()} helperText={requiresComment(selectedStatus) && !commentText.trim() ? 'Comment is required for this status' : ''}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {requiresComment(selectedStatus) ? <>Comments are required for status changes to: Not Delivered, Cancelled, Return, and Delivery statuses.<br /><strong>Suggested comment:</strong> Status updated to: {ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus)?.label || getStatusText(selectedStatus)}</> : 'Comments are optional for this status change.'}
          </Typography>
          {selectedOrder && hasCommentsForStatus(selectedOrder.id, selectedStatus) && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, maxHeight: 200, overflowY: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>Existing comments for this status:</Typography>
              {getCommentsForStatus(selectedOrder.id, selectedStatus).slice().sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()).map((comment, index) => (
                <CommentItem key={comment.id || index} comment={comment} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCommentDialogClose} disabled={isUpdating}>Cancel</Button>
          <Button variant="contained" onClick={handleCommentSubmit} disabled={isUpdating || (requiresComment(selectedStatus) && !commentText.trim())} startIcon={isUpdating ? <CircularProgress size={20} /> : null}>
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
          <Button onClick={handleCommentDialogClose} disabled={isUpdating}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Comments Dialog */}
      <Dialog open={viewCommentsOpen} onClose={() => setViewCommentsOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>All Comments for Order #{selectedOrder?.id}</DialogTitle>
        <DialogContent>
          {selectedOrder && <CommentsList orderId={selectedOrder.id} maxHeight={500} showDeleteAllButton={true} />}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setViewCommentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Comments Dialog */}
      <Dialog open={deleteAllCommentsDialogOpen} onClose={() => setDeleteAllCommentsDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Confirm Delete All Comments</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>Are you sure you want to delete all comments for Order #{selectedOrder?.id}?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {deletableComments.length === (selectedOrder?.comments?.length || 0)
              ? `This will delete ${deletableComments.length} comment${deletableComments.length !== 1 ? 's' : ''} permanently. This action cannot be undone.`
              : `This will delete ${deletableComments.length} of ${selectedOrder?.comments?.length || 0} comment${(selectedOrder?.comments?.length || 0) !== 1 ? 's' : ''} permanently (only comments with statuses 5–17 are deletable). This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteAllCommentsDialogOpen(false)} disabled={isDeletingComment}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteAllComments} disabled={isDeletingComment || deletableComments.length === 0} startIcon={isDeletingComment ? <CircularProgress size={20} /> : null}>
            {isDeletingComment ? 'Deleting...' : 'Delete All Comments'}
          </Button>
          <Button onClick={() => setDeleteAllCommentsDialogOpen(false)} disabled={isDeletingComment}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Order Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Confirm Delete Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>Are you sure you want to delete Order #{selectedOrder?.id}?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>This action cannot be undone. All order data, including items and comments, will be permanently deleted.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteOrder} disabled={isDeleting} startIcon={isDeleting ? <CircularProgress size={20} /> : null}>
            {isDeleting ? 'Deleting...' : 'Delete Order'}
          </Button>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement;